import { useEffect, useState, useRef } from "react";
import { BrowseFilterSection } from "../components/Browse/BrowseFilterSection";
import { BrowseLayout } from "../components/Browse/BrowseLayout";
import { LisitngGrid } from "../components/Browse/ListingGrid";
import { fetchAllListings } from "../api/listingApi";
import { FilterProvider, useFilters } from "../contexts/FilterContext";

/**
 * Inner component — has access to the filters via context.
 * Re-fetches listings whenever the location filter or radius changes.
 */
const BrowseContent = () => {
  const { filters } = useFilters();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  // Debounce timer for radius slider — wait 400ms after the user stops dragging
  // before re-fetching, so we don't hammer the backend on every pixel of movement.
  const debounceRef = useRef(null);

  useEffect(() => {
    // Clear any pending fetch
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        // Build query params based on current location filter state
        const params = {};
        if (filters.useLocationFilter && filters.userCoordinates) {
          const [lng, lat] = filters.userCoordinates;
          params.lat = lat;
          params.lng = lng;
          params.radius = filters.radius;
        }

        const res = await fetchAllListings(params);
        if (res.success) {
          setListings(res.listings);
        }
      } catch (err) {
        console.error("Failed to fetch listings", err);
      } finally {
        setLoading(false);
      }
    }, 400);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [
    filters.useLocationFilter,
    filters.userCoordinates,
    filters.radius,
  ]);

  return (
    <>
      <BrowseFilterSection />

      {loading ? (
        <p>Loading listings...</p>
      ) : listings.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          {filters.useLocationFilter
            ? `No listings found within ${Math.round(
                filters.radius / 1000
              )}km. Try expanding the radius or changing your location.`
            : "No listings found."}
        </div>
      ) : (
        <LisitngGrid listings={listings} />
      )}
    </>
  );
};

const Browse = () => {
  return (
    <FilterProvider>
      <BrowseLayout>
        <BrowseContent />
      </BrowseLayout>
    </FilterProvider>
  );
};

export default Browse;